// QR scanner for the TESTIFY exam-school link. Two decoders, picked at runtime:
//   1. BarcodeDetector — native, zero cost, present on Android Chrome (our main
//      target). Not in iOS Safari, and unreliable in some Android WebViews.
//   2. jsQR — pure-JS fallback (~13 KB gzipped), works everywhere and offline.
// Also offers a photo fallback, since a QR on a printed page can be shot with the
// file picker when the live camera is blocked (iOS PWA permission quirks).
import jsQR from 'jsqr';
import { getStream, stopStream, frameToImageData, fileToImageData } from './camera.js';
import { esc } from './dom.js';
import { icon } from './icons.js';

type Detected = { rawValue: string };
interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<Detected[]>;
}
type BarcodeDetectorCtor = new (opts: { formats: string[] }) => BarcodeDetectorLike;

/** Native detector when the platform has it AND it lists qr_code as supported. */
async function nativeDetector(): Promise<BarcodeDetectorLike | null> {
  const Ctor = (window as unknown as { BarcodeDetector?: BarcodeDetectorCtor & { getSupportedFormats?: () => Promise<string[]> } })
    .BarcodeDetector;
  if (!Ctor) return null;
  try {
    const formats = (await Ctor.getSupportedFormats?.()) ?? ['qr_code'];
    if (!formats.includes('qr_code')) return null;
    return new Ctor({ formats: ['qr_code'] });
  } catch {
    return null;
  }
}

/** Decode a still frame with jsQR (fallback path and photo path). */
function decodeImageData(img: ImageData): string | null {
  const found = jsQR(img.data, img.width, img.height, { inversionAttempts: 'attemptBoth' });
  return found?.data?.trim() || null;
}

/**
 * Open the scanner. Resolves with the decoded QR text, or null if the teacher
 * cancels. Never rejects — camera failures fall back to the photo picker.
 */
export function scanQrDialog(title = 'Scan the TESTIFY QR code'): Promise<string | null> {
  return new Promise((resolve) => {
    const dlg = document.createElement('dialog');
    dlg.className = 'dlg';
    dlg.innerHTML = `<div>
      <h2 class="dlg-title">${esc(title)}</h2>
      <div class="camera-wrap qr-wrap"><video id="qrvid" playsinline muted autoplay></video><div class="camera-guide"></div></div>
      <p class="hint" id="qrmsg">Point the camera at the QR code — it reads automatically.</p>
      <input type="file" id="qrfile" accept="image/*" capture="environment" hidden>
      <div class="dlg-actions">
        <button type="button" class="btn btn-ghost" data-cancel>Cancel</button>
        <button type="button" class="btn btn-ghost" data-photo>${icon.camera()} Use a photo</button>
      </div></div>`;
    document.body.appendChild(dlg);

    const video = dlg.querySelector<HTMLVideoElement>('#qrvid')!;
    const msg = dlg.querySelector<HTMLParagraphElement>('#qrmsg')!;
    const fileEl = dlg.querySelector<HTMLInputElement>('#qrfile')!;
    let raf = 0;
    let done = false;

    const finish = (value: string | null) => {
      if (done) return;
      done = true;
      cancelAnimationFrame(raf);
      stopStream();
      video.srcObject = null;
      dlg.close();
      dlg.remove();
      resolve(value);
    };

    dlg.querySelector('[data-cancel]')!.addEventListener('click', () => finish(null));
    dlg.addEventListener('cancel', () => finish(null));
    dlg.querySelector('[data-photo]')!.addEventListener('click', () => fileEl.click());
    fileEl.addEventListener('change', async () => {
      const file = fileEl.files?.[0];
      if (!file) return;
      msg.textContent = 'Reading photo…';
      // A QR fills much less of the frame in a photo — keep more pixels than the
      // live-preview path so a small printed code still resolves.
      const hit = decodeImageData(await fileToImageData(file, 1600));
      if (hit) return finish(hit);
      msg.innerHTML = `<span class="danger-text">No QR code found in that photo. Try again, closer and in better light.</span>`;
    });

    (async () => {
      let detector: BarcodeDetectorLike | null = null;
      try {
        const stream = await getStream();
        video.srcObject = stream;
        await video.play().catch(() => {});
        detector = await nativeDetector();
      } catch {
        msg.innerHTML = `<span class="danger-text">Camera unavailable — use “Use a photo” instead.</span>`;
        return;
      }

      // Throttle to ~8 reads/sec: decoding every frame burns battery for nothing.
      let last = 0;
      const tick = async (now: number) => {
        if (done) return;
        raf = requestAnimationFrame(tick);
        if (now - last < 120 || video.readyState < 2) return;
        last = now;
        try {
          if (detector) {
            const hits = await detector.detect(video);
            const value = hits.find((h) => h.rawValue?.trim())?.rawValue.trim();
            if (value) return finish(value);
          } else {
            // 800px is plenty for a QR and keeps the pure-JS decode fast.
            const hit = decodeImageData(frameToImageData(video, 800));
            if (hit) return finish(hit);
          }
        } catch {
          // A failed detect on one frame is normal — keep scanning.
        }
      };
      raf = requestAnimationFrame(tick);
    })();

    dlg.showModal();
  });
}
